import {
    Box,
    Typography,
    Button,
    styled,
    TextField,
    MenuItem,
    Chip,
    Autocomplete
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Progress from "../../../components/progress/Progress";
import "../../../assets/css/time.css";
import ITask, { ETaskType } from "../../../models/task";
import objectUtils from "../../../utils/objectUtils";
import IProfile from "../../../models/profile";
import { useEffect, useState } from "react";
import profileService from "../../../services/profile/ProfileService";
import { useTranslatedTaskName } from "../../../utils/taskNameTranslation";
import EditOnly from "../../../components/permissions/EditOnly";

const EntryTitle = styled(Typography)(({ theme }) => ({
    fontSize: "0.8em",
    fontWeight: 600,
    display: "flex",
    flex: 0.3,
    marginLeft: theme.spacing(1),
}));

const ProfileWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    marginTop: "1em",
}));

const EntryWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "70%",
}));

const EntryField = styled(Box)(({ theme }) => ({
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "1em",
}));

interface TaskSelectorProps {
    selectedTasks: number[];
    updateSelectedTasks: (value: (string | number)[]) => void;
    tasks: ITask[];
}

const TaskSelector = ({ selectedTasks, updateSelectedTasks, tasks }: TaskSelectorProps) => {
    const translateTaskName = useTranslatedTaskName();
    const sortedTasks = [...tasks].sort((a, b) =>
        translateTaskName(a).localeCompare(translateTaskName(b))
    );
    return (
        <Autocomplete
            fullWidth
            inputMode="search"
            multiple
            value={sortedTasks.filter((task) => selectedTasks.includes(task.id))}
            onChange={(_, values) => updateSelectedTasks(values.map((task) => task.id))}
            getOptionLabel={(option) => translateTaskName(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={({ inputProps, ...params }) => (
                <TextField
                    {...params}
                    inputProps={{
                        ...inputProps,
                        style: { fontSize: "0.875rem" },
                    }}
                    fullWidth
                    InputLabelProps={{
                        children: null,
                    }}
                />
            )}
            renderOption={(props, option) => (
                <MenuItem {...props}>
                    <Typography variant="body2">{translateTaskName(option)}</Typography>
                </MenuItem>
            )}
            options={sortedTasks}
            renderTags={(values, getTagProps) =>
                values.map((task, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                        <Chip label={translateTaskName(task)} key={key} {...tagProps} />
                    );
                })
            }
        />
    );
}

interface AddProfileProps {
    profile: IProfile;
    onInputChange: Function;
    handleProfile: Function;
    isFormSending: boolean;
    isCreation: boolean;
    isProfileChanged?: boolean;
    selectedTasksIds?: number[];
}

const AddProfile = ({
    profile,
    onInputChange,
    handleProfile,
    isFormSending,
    isCreation,
    isProfileChanged,
    selectedTasksIds,
}: AddProfileProps) => {
    const { t } = useTranslation();

    const [selectedPeriodics, setSelectedPeriodics] = useState<number[]>([]);
    const [selectedWeeklies, setSelectedWeeklies] = useState<number[]>([]);
    const [tasks, setTasks] = useState<ITask[]>([]);

    const periodics = tasks.filter((task) => task.type === ETaskType.Periodic)
    const weeklies = tasks.filter((task) => task.type === ETaskType.Weekly)

    const updateSelectedTasks = (
        value: (string | number)[],
        type: ETaskType
    ) => {

        const selectedTaskIds = value.map(Number);

        if (type === ETaskType.Periodic) {
            setSelectedPeriodics(selectedTaskIds)
            onInputChange("tasks", [...selectedTaskIds, ...selectedWeeklies]);
        }
        if (type === ETaskType.Weekly) {
            setSelectedWeeklies(selectedTaskIds)
            onInputChange("tasks", [...selectedTaskIds, ...selectedPeriodics]);
        }
    };

    useEffect(() => {
        profileService.GetTasks().then(setTasks);
    }, []);

    useEffect(() => {

        const selectedPeriodicsIds = selectedTasksIds?.filter((taskId) => periodics.some((periodic) => periodic.id === taskId)) || []
        setSelectedPeriodics(selectedPeriodicsIds)

        const selectedWeekliesIds = selectedTasksIds?.filter((taskId) => weeklies.some((weekly) => weekly.id === taskId)) || []
        setSelectedWeeklies(selectedWeekliesIds)
    }, [selectedTasksIds, tasks]);

    return (
        <ProfileWrapper>
            <EntryWrapper>
                <EntryTitle>{t("table.profile.name")}</EntryTitle>
                <EntryField>
                    <TextField
                        name="name"
                        required={true}
                        style={{ textAlign: "left", width: "100%" }}
                        value={profile.name}
                        onChange={(e) => onInputChange("name", e.target.value)}
                    />
                </EntryField>
            </EntryWrapper>

            <EntryWrapper>
                <EntryTitle>{t("table.profile.description")}</EntryTitle>
                <EntryField>
                    <TextField
                        name="description"
                        required={true}
                        style={{ textAlign: "left", width: "100%" }}
                        value={profile.description}
                        onChange={(e) =>
                            onInputChange("description", e.target.value)
                        }
                    />
                </EntryField>
            </EntryWrapper>

            <EntryWrapper>
                <EntryTitle>{t("table.profile.periodics")} </EntryTitle>
                <EntryField>
                    <TaskSelector selectedTasks={selectedPeriodics} updateSelectedTasks={(value) => updateSelectedTasks(value, ETaskType.Periodic)} tasks={periodics} />
                </EntryField>
            </EntryWrapper>

            <EntryWrapper>
                <EntryTitle>{t("table.profile.weeklies")} </EntryTitle>
                <EntryField>
                    <TaskSelector selectedTasks={selectedWeeklies} updateSelectedTasks={(value) => updateSelectedTasks(value, ETaskType.Weekly)} tasks={weeklies} />
                </EntryField>
            </EntryWrapper>

            <EntryWrapper sx={{ justifyContent: "flex-end" }}>
                <Progress isLoading={isFormSending}>
                    <EditOnly>
                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            disabled={!profile.name || (isProfileChanged != undefined && !isProfileChanged)}
                            onClick={() => handleProfile()}
                            sx={{
                                marginTop: "0.5em",
                                marginBottom: "0.5em",
                                marginLeft: "0",
                            }}
                        >
                            {isCreation
                                ? t("table.profile.create")
                                : t("table.profile.edit")}
                        </Button>
                    </EditOnly>
                </Progress>
            </EntryWrapper>
        </ProfileWrapper>
    );
};

export default AddProfile;
